import _ from 'lodash'
import WidgetView from './widget-view'
import Dataset from 'scoreboard/datasets/model/dataset'
import { fieldNotReady } from './dataset-widget-not-ready'
import LoadingDots from 'scoreboard/components/loading-dots'
import { getEmptyStateOptions } from './handle-dataset-widget-error'

/**
 * Common functionality for widgets that have a data source (#52717)
 */
const DataSourceWidgetView = WidgetView.extend({
	/**
	 * Handlebars template for the widget
	 *
	 * @abstract
	 */
	template: null,

	render: function () {
		WidgetView.prototype.render.apply(this, arguments)

		if (this.el.children.length === 0) {
			this.$el.append(this.template())
			this.loadingDots = new LoadingDots({
				el: this.$('.loading-dots-container')[0]
			})
		}

		this.renderContentIfNeeded({
			// Use the last datasetQueryResponse (if we have one already) so
			// that re-renders that don't require a new query (like changing
			// the color) can be rendered immediately
			datasetQueryResponse: this.lastDatasetQueryResponse
		})

		return this
	},

	/**
	 * Renders the content of the widget if needed (has exceptions for
	 * datasets in error state, etc.)
	 *
	 * @param {Object} options
	 * @param {DatasetQueryValueResponse} [options.datasetQueryResponse]
	 * if a dataset source, the response from the dataset query
	 */
	renderContentIfNeeded ({ datasetQueryResponse } = {}) {
		if (this.model.isDatasetSource() && _.isNil(datasetQueryResponse)) {
			if (!this.rendered) {
				// If this is a dataset datasource, the initial render (with no
				// datasetQueryResponse) will just render the basics. maestro will
				// take care of setting loading dots and calling renderDatasetResponse()
				//
				// For now, just say the widget is ready so the dashboard can
				// display (and the user can see loading dots). But it isn't ready
				// for export yet
				this.renderPlaceholderContent?.()
				this.readyForDisplay()
			}

			// Whether or not this is the first render, we definitely don't
			// want to go any further for a dataset source without a
			// datasetQueryResponse (could be first render or an error query)
			return
		}

		if (this.isInDatasetErrorState()) {
			// If the dataset is in an error state, we don't want to render
			// the shape; instead, let maestro and renderDatasetError() take
			// care of it
			return
		}

		this.lastDatasetQueryResponse = datasetQueryResponse

		// We need this set on the model so that we can provide default
		// thresholds in the "Set Thresholds" dialog
		this.model.set('datasetQueryResponse',
			datasetQueryResponse, { silent: true })
		this.renderContent({ datasetQueryResponse })
		this.rendered = true
	},

	/**
	 * For shapes with a dataset datasource, returns the dataset filters.
	 */
	filters () {
		// Note: unlike in dataset-filter-widget-view, there's no need to assign
		// a linkGroup here, because manual filters will either not have them,
		// or they will be remapped later in maestro
		return this.model.filters()
	},

	/**
	 * Called by maestro after a dataset query has completed.
	 *
	 * @param {DatasetQueryValueResponse} datasetQueryResponse the response from
	 * the dataset query
	 */
	renderDatasetResponse: function (datasetQueryResponse) {
		if (this.isInDatasetErrorState()) {
			this.renderDatasetError()
		} else {
			this.renderContentIfNeeded({datasetQueryResponse})
		}

		this.clearLoadingDots()
	},

	/**
	 * Returns true if this widget has a dataset source and the dataset (or
	 * field) is in error state.
	 */
	isInDatasetErrorState () {
		if (!this.model.isDatasetSource()) {
			return
		}

		let dataset = this.model.dataset()
		let fieldId =
			this.model.properties.get('fieldId') ?? Dataset.TOTAL
		let field = Dataset.isTotal(fieldId)
			? null : dataset.getField(fieldId)
		return fieldNotReady(dataset, field)
	},

	/**
	 * Renders the dataset error/empty state for the widget.
	 *
	 * @param {ResolvableExceptionResponse} errorResponse The response from the server
	 * containing the error. Object expected to include properties like
	 * `messageCode`, `arguments`, and `stackTrace`
	 */
	renderDatasetError (errorResponse) {
		this.lastDatasetQueryResponse = null
		let emptyStateOptions = getEmptyStateOptions({
			datasetModel: this.model.dataset(),
			errorResponse
		})
		this.toggleEmptyState(null, emptyStateOptions)

		this.clearLoadingDots()
		this.rendered = true
	},

	/**
	 * Renders the content of the widget (eg the shape, the speedometer, etc)
	 *
	 * @param {Object} options
	 *
	 * @param {DatasetQueryValueResponse} [options.datasetQueryResponse]
	 * if a dataset source, the response from the dataset query
	 *
	 * @abstract
	 */
	renderContent ({ datasetQueryResponse } = {}) {
	},

	loading () {
		this.loadingDots?.render()
		this.$el.addClass('is-loading')
	},

	clearLoadingDots() {
		this.$el.removeClass('is-loading')
		this.loadingDots?.clear()
	},

	/**
	 * @override
	 */
	ignoreChangeEvents () {
		// Some widgets make adjustments the first time they render, and
		// for dataset widgets, that doesn't happen until the dataset query
		// XHR returns. So this prevents adjustments like that from making
		// the dashboard form dirty
		return !this.rendered
	},

})

export default DataSourceWidgetView

DataSourceWidgetView.__filename__ = 'dashboards/widgets/data-source-widget-view.js'
